import React, { useState, useEffect, useRef, useCallback } from 'react';
import './GamifiedPractice.css';

// Utility Functions
const capitalizeFirst = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1) : '';
const normalizeAnswerForComparison = (answer) => {
    if (answer === undefined || answer === null) return 'A';
    if (typeof answer === 'string' && answer.toUpperCase().match(/^[A-D]$/)) return answer.toUpperCase();
    if (typeof answer === 'number' && answer >= 0 && answer <= 3) return String.fromCharCode(65 + answer);
    if (typeof answer === 'string' && answer.match(/^[0-3]$/)) return String.fromCharCode(65 + parseInt(answer));
    return 'A';
};
const extractYouTubeId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
};
const getPointsForLevel = (level) => {
    const pointsMap = { simple: 10, medium: 40, hard: 50, adaptive: 25 };
    return pointsMap[level] || 10;
};

// CSS Avatar Component
const MathBuddyAvatar = ({ isSpeaking, expression, onClick, speechText, visible }) => {
    if (!visible) return null;
    return (
        <div className="avatar-wrapper" onClick={onClick}>
            <div className={`buddy-avatar ${isSpeaking ? 'speaking' : ''} ${expression || 'normal'}`}>
                <div className="avatar-glow-ring"></div>
                <div className="buddy-head">
                    <div className="buddy-antenna"><div className="antenna-ball"></div></div>
                    <div className="buddy-face">
                        <div className="buddy-eyes">
                            <div className={`buddy-eye left-eye ${expression === 'happy' ? 'happy' : expression === 'thinking' ? 'thinking' : expression === 'excited' ? 'excited' : expression === 'sad' ? 'sad' : 'normal'}`}>
                                <div className="eye-pupil"><div className="eye-shine"></div></div>
                                {expression === 'happy' && <div className="eye-sparkle">✦</div>}
                            </div>
                            <div className={`buddy-eye right-eye ${expression === 'happy' ? 'happy' : expression === 'thinking' ? 'thinking' : expression === 'excited' ? 'excited' : expression === 'sad' ? 'sad' : 'normal'}`}>
                                <div className="eye-pupil"><div className="eye-shine"></div></div>
                            </div>
                        </div>
                        <div className="buddy-nose"></div>
                        <div className={`buddy-mouth ${isSpeaking ? 'talking' : ''} ${expression || 'normal'}`}><div className="mouth-inner"></div></div>
                    </div>
                    <div className="buddy-ear left-ear"><div className="ear-inner"></div></div>
                    <div className="buddy-ear right-ear"><div className="ear-inner"></div></div>
                </div>
                <div className="buddy-body">
                    <div className="body-badge">∑</div>
                    <div className="body-buttons"><div className="body-btn"></div><div className="body-btn"></div></div>
                </div>
                <div className={`buddy-arm left-arm ${isSpeaking ? 'wave' : ''}`}><div className="arm-hand">👋</div></div>
                <div className="buddy-arm right-arm"><div className="arm-hand">🔢</div></div>
                <div className="buddy-feet">
                    <div className="buddy-foot left-foot"></div>
                    <div className="buddy-foot right-foot"></div>
                </div>
            </div>
            {speechText && (
                <div className="avatar-speech-bubble">
                    <div className="speech-bubble-content">{speechText}</div>
                    <div className="speech-bubble-tail"></div>
                </div>
            )}
            <div className="avatar-label">SolveSmart ✨</div>
        </div>
    );
};

// ============================================================
// WorkbookGenerator — PDF via print window
// ============================================================
const WorkbookGenerator = ({ isOpen, onClose, questions, totalScore, accuracy, concept, subconcept, difficulty, levelNumber, userGrade }) => {
    const [includeAnswers, setIncludeAnswers] = useState(true);
    const [includeExplanation, setIncludeExplanation] = useState(true);

    const downloadPDF = () => {
        if (!questions || questions.length === 0) {
            alert('No questions available to generate workbook!');
            return;
        }

        const date = new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
        const optionLetters = ['A', 'B', 'C', 'D'];
        const optionColors = ['#6366f1', '#8b5cf6', '#06b6d4', '#10b981'];

        const questionsHTML = questions.map((q, idx) => {
            const opts = Array.isArray(q.options) ? q.options.slice(0, 4) : ['A', 'B', 'C', 'D'].map(k => q.options?.[k] || '');
            const correctLetter = (q.correct_answer || 'A').toUpperCase();

            const optionsHTML = opts.map((opt, i) => {
                const letter = optionLetters[i];
                const isCorrect = includeAnswers && correctLetter === letter;
                return `
                <div class="option ${isCorrect ? 'correct-option' : ''}">
                    <span class="opt-badge" style="background:${isCorrect ? '#10b981' : optionColors[i] + '18'}; color:${isCorrect ? '#fff' : optionColors[i]}; border:1.5px solid ${isCorrect ? '#10b981' : optionColors[i] + '44'};">
                        ${letter}
                    </span>
                    <span class="opt-text">${opt || `Option ${letter}`}</span>
                    ${isCorrect ? '<span class="correct-tick">✓ Correct</span>' : ''}
                </div>`;
            }).join('');

            const explanationHTML = (includeExplanation && q.explanation) ? `
                <div class="explanation-box">
                    <div class="exp-label">📖 Explanation</div>
                    <div class="exp-text">${q.explanation}</div>
                </div>` : '';

            return `
            <div class="question-card">
                <div class="q-header">
                    <div class="q-num-badge">Q${idx + 1}</div>
                    ${q.concept ? `<div class="q-concept-tag">${q.concept}</div>` : ''}
                </div>
                <div class="q-text">${q.question}</div>
                <div class="options-wrap">${optionsHTML}</div>
                ${explanationHTML}
            </div>`;
        }).join('');

        const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8"/>
<title>SolveSmart Workbook</title>
<style>
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
    --ink: #0f172a;
    --ink2: #334155;
    --ink3: #64748b;
    --rule: #e2e8f0;
    --accent: #6366f1;
    --accent2: #8b5cf6;
    --green: #10b981;
    --green-bg: #ecfdf5;
    --paper: #ffffff;
    --shade: #f8fafc;
}

html { font-size: 13px; }

body {
    font-family: 'Plus Jakarta Sans', -apple-system, sans-serif;
    color: var(--ink);
    background: #f1f5f9;
    padding: 24px 16px 48px;
    line-height: 1.5;
}

.page {
    max-width: 760px;
    margin: 0 auto;
    background: var(--paper);
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 8px 48px rgba(0,0,0,0.12);
}

/* ── COVER ── */
.cover {
    background: linear-gradient(135deg, #1e1b4b 0%, #312e81 40%, #4c1d95 100%);
    padding: 40px 40px 32px;
    position: relative;
    overflow: hidden;
}
.cover::before {
    content: '';
    position: absolute;
    top: -60px; right: -60px;
    width: 240px; height: 240px;
    border-radius: 50%;
    background: rgba(255,255,255,0.05);
}
.cover::after {
    content: '';
    position: absolute;
    bottom: -40px; left: 30%;
    width: 160px; height: 160px;
    border-radius: 50%;
    background: rgba(99,102,241,0.3);
}
.cover-brand {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.55);
    margin-bottom: 20px;
}
.cover-title {
    font-size: 30px;
    font-weight: 800;
    color: #fff;
    line-height: 1.2;
    margin-bottom: 8px;
}
.cover-subtitle {
    font-size: 14px;
    color: rgba(255,255,255,0.65);
    font-weight: 500;
    margin-bottom: 28px;
}
.cover-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    position: relative;
    z-index: 1;
}
.pill {
    background: rgba(255,255,255,0.12);
    border: 1px solid rgba(255,255,255,0.2);
    color: rgba(255,255,255,0.9);
    border-radius: 100px;
    padding: 4px 14px;
    font-size: 11.5px;
    font-weight: 600;
    letter-spacing: 0.02em;
}
.pill.accent { background: rgba(99,102,241,0.5); border-color: rgba(99,102,241,0.7); }

/* ── STATS BAR ── */
.stats-bar {
    display: flex;
    border-bottom: 1px solid var(--rule);
}
.stat-cell {
    flex: 1;
    padding: 18px 20px;
    border-right: 1px solid var(--rule);
    text-align: center;
}
.stat-cell:last-child { border-right: none; }
.stat-val {
    font-size: 22px;
    font-weight: 800;
    color: var(--accent);
    line-height: 1;
    margin-bottom: 4px;
}
.stat-key {
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--ink3);
}

/* ── SECTION LABEL ── */
.section-label {
    padding: 20px 32px 0;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--ink3);
    display: flex;
    align-items: center;
    gap: 8px;
}
.section-label::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--rule);
}

/* ── QUESTION CARDS ── */
.questions-wrap {
    padding: 16px 32px 32px;
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.question-card {
    border: 1.5px solid var(--rule);
    border-radius: 14px;
    overflow: hidden;
    page-break-inside: avoid;
}

.q-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 18px;
    background: var(--shade);
    border-bottom: 1px solid var(--rule);
}

.q-num-badge {
    background: linear-gradient(135deg, var(--accent), var(--accent2));
    color: #fff;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.04em;
    border-radius: 8px;
    padding: 3px 10px;
    min-width: 36px;
    text-align: center;
}

.q-concept-tag {
    font-size: 11px;
    font-weight: 600;
    color: var(--ink3);
    background: #fff;
    border: 1px solid var(--rule);
    border-radius: 100px;
    padding: 2px 10px;
}

.q-text {
    padding: 14px 18px 12px;
    font-size: 13.5px;
    font-weight: 600;
    color: var(--ink);
    line-height: 1.55;
}

.options-wrap {
    padding: 4px 18px 14px;
    display: flex;
    flex-direction: column;
    gap: 7px;
}

.option {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    border-radius: 8px;
    background: var(--shade);
    border: 1px solid transparent;
    transition: all 0.15s;
}

.correct-option {
    background: var(--green-bg);
    border-color: #6ee7b7;
}

.opt-badge {
    width: 26px;
    height: 26px;
    border-radius: 7px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11.5px;
    font-weight: 800;
    flex-shrink: 0;
}

.opt-text {
    font-size: 12.5px;
    font-weight: 500;
    color: var(--ink2);
    flex: 1;
}

.correct-tick {
    font-size: 10.5px;
    font-weight: 700;
    color: var(--green);
    background: #d1fae5;
    border-radius: 100px;
    padding: 2px 9px;
    white-space: nowrap;
}

/* ── EXPLANATION ── */
.explanation-box {
    margin: 0 18px 14px;
    background: #fefce8;
    border: 1px solid #fde68a;
    border-radius: 10px;
    padding: 12px 14px;
}
.exp-label {
    font-size: 11px;
    font-weight: 700;
    color: #92400e;
    letter-spacing: 0.04em;
    margin-bottom: 5px;
}
.exp-text {
    font-size: 12px;
    color: #78350f;
    line-height: 1.6;
}

/* ── FOOTER ── */
.workbook-footer {
    padding: 20px 32px;
    background: var(--shade);
    border-top: 1px solid var(--rule);
    display: flex;
    justify-content: space-between;
    align-items: center;
}
.footer-brand {
    font-size: 12px;
    font-weight: 700;
    color: var(--accent);
    letter-spacing: 0.04em;
}
.footer-date {
    font-size: 11px;
    color: var(--ink3);
}
.footer-note {
    font-size: 10.5px;
    color: var(--ink3);
    font-style: italic;
}

/* ── PRINT ── */
@media print {
    body { background: #fff; padding: 0; }
    .page { box-shadow: none; border-radius: 0; max-width: 100%; }
    .question-card { page-break-inside: avoid; }
    .cover { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .q-num-badge { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .correct-option { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .opt-badge { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .stats-bar { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
}
</style>
</head>
<body>
<div class="page">

  <!-- COVER -->
  <div class="cover">
    <div class="cover-brand">SolveSmart ✨ — Practice Workbook</div>
    <div class="cover-title">${concept || 'Mathematics'}</div>
    <div class="cover-subtitle">${subconcept || 'General Practice'}</div>
    <div class="cover-pills">
      <span class="pill accent">Level ${levelNumber || 1}</span>
      <span class="pill">${capitalizeFirst(difficulty) || 'Medium'} Difficulty</span>
      <span class="pill">Grade ${userGrade}</span>
      <span class="pill">${questions.length} Questions</span>
      <span class="pill">${date}</span>
    </div>
  </div>

  <!-- STATS BAR -->
  <div class="stats-bar">
    <div class="stat-cell">
      <div class="stat-val">${questions.length}</div>
      <div class="stat-key">Questions</div>
    </div>
    <div class="stat-cell">
      <div class="stat-val">${capitalizeFirst(difficulty) || 'Medium'}</div>
      <div class="stat-key">Difficulty</div>
    </div>
    <div class="stat-cell">
      <div class="stat-val">Lvl ${levelNumber || 1}</div>
      <div class="stat-key">Level</div>
    </div>
    <div class="stat-cell">
      <div class="stat-val">G${userGrade}</div>
      <div class="stat-key">Grade</div>
    </div>
  </div>

  <!-- QUESTIONS -->
  <div class="section-label">Questions</div>
  <div class="questions-wrap">
    ${questionsHTML}
  </div>

  <!-- FOOTER -->
  <div class="workbook-footer">
    <span class="footer-brand">SolveSmart ✨</span>
    <span class="footer-note">${includeAnswers ? 'Answer key included' : 'No answer key'} · ${includeExplanation ? 'Explanations included' : 'No explanations'}</span>
    <span class="footer-date">${date}</span>
  </div>

</div>

<script>
  window.onload = function() {
    window.print();
  };
</script>
</body>
</html>`;

        const printWindow = window.open('', '_blank', 'width=900,height=700');
        if (!printWindow) {
            alert('Popup blocked! Please allow popups for this site and try again.');
            return;
        }
        printWindow.document.write(html);
        printWindow.document.close();
    };

    if (!isOpen) return null;

    return (
        <div className="workbook-overlay" onClick={onClose}>
            <div className="workbook-popup" onClick={e => e.stopPropagation()}>
                <button className="workbook-close" onClick={onClose}>✕</button>

                <div className="workbook-header">
                    <span className="workbook-icon">📚</span>
                    <h2>Download Practice Workbook</h2>
                    <p>Create a printable PDF worksheet from your current questions</p>
                </div>

                {/* Stats Preview */}
                <div className="workbook-stats-preview">
                    <div className="stat-card">
                        <span className="stat-emoji">📝</span>
                        <div>
                            <div className="stat-number">{questions?.length || 0}</div>
                            <div className="stat-label">Questions</div>
                        </div>
                    </div>
                    <div className="stat-card">
                        <span className="stat-emoji">🎯</span>
                        <div>
                            <div className="stat-number">{capitalizeFirst(difficulty)}</div>
                            <div className="stat-label">Difficulty</div>
                        </div>
                    </div>
                    <div className="stat-card">
                        <span className="stat-emoji">📖</span>
                        <div>
                            <div className="stat-number">{concept}</div>
                            <div className="stat-label">Concept</div>
                        </div>
                    </div>
                </div>

                {/* Options */}
                <div className="workbook-options">
                    <h3>Workbook Options</h3>
                    <label className="workbook-checkbox">
                        <input type="checkbox" checked={includeAnswers} onChange={(e) => setIncludeAnswers(e.target.checked)} />
                        <span>✅ Include Correct Answers</span>
                    </label>
                    <label className="workbook-checkbox">
                        <input type="checkbox" checked={includeExplanation} onChange={(e) => setIncludeExplanation(e.target.checked)} />
                        <span>📖 Include Explanations</span>
                    </label>
                </div>

                {/* Question Preview */}
                <div className="workbook-preview-questions">
                    <h3>Preview (First 3 Questions)</h3>
                    <div className="preview-list">
                        {questions?.slice(0, 3).map((q, idx) => (
                            <div key={idx} className="preview-question">
                                <div className="preview-q-num">Q{idx + 1}</div>
                                <div className="preview-q-text">{q.question?.substring(0, 100)}...</div>
                            </div>
                        ))}
                        {questions?.length > 3 && (
                            <div className="preview-more">+{questions.length - 3} more questions</div>
                        )}
                    </div>
                </div>

                {/* Actions */}
                <div className="workbook-actions">
                    <button className="workbook-download-btn" onClick={downloadPDF}>
                        🖨️ Generate PDF
                    </button>
                    <button className="workbook-cancel-btn" onClick={onClose}>
                        Cancel
                    </button>
                </div>

                <p className="workbook-note">
                    ℹ️ A print preview will open — select "Save as PDF" in the print dialog to download.
                </p>
            </div>
        </div>
    );
};

// Main Component
const GamifiedPractice = () => {
    const [gameState, setGameState] = useState('levelSelection');
    const [currentLevel, setCurrentLevel] = useState('');
    const [currentDifficulty, setCurrentDifficulty] = useState(1);
    const [currentLevelNumber, setCurrentLevelNumber] = useState(1);
    const [currentSubLevel, setCurrentSubLevel] = useState(1);
    const [currentQuestions, setCurrentQuestions] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [totalScore, setTotalScore] = useState(0);
    const [lives, setLives] = useState(3);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [questionAnswered, setQuestionAnswered] = useState(false);
    const [currentAttempt, setCurrentAttempt] = useState(0);
    const [subLevelCorrectAnswers, setSubLevelCorrectAnswers] = useState(0);
    const [subLevelTotalQuestions, setSubLevelTotalQuestions] = useState(0);
    const [voiceEnabled, setVoiceEnabled] = useState(true);
    const [chatbotActive, setChatbotActive] = useState(false);
    const [chatMessages, setChatMessages] = useState([
        { type: 'bot', content: "Hi! I'm SolveSmart! 🎓 I'm here to help you master math!", id: Date.now() }
    ]);
    const [videoModal, setVideoModal] = useState({ open: false, url: '', title: '' });
    const [chatInput, setChatInput] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [showAvatar, setShowAvatar] = useState(true);
    const [avatarSpeech, setAvatarSpeech] = useState('');
    const [avatarExpression, setAvatarExpression] = useState('normal');
    const [isSpeakingAvatar, setIsSpeakingAvatar] = useState(false);
    const [showHelpPopup, setShowHelpPopup] = useState(false);
    const [currentQuestionForHelp, setCurrentQuestionForHelp] = useState(null);
    const [adaptiveMode, setAdaptiveMode] = useState(false);
    const [currentAdaptiveDifficulty, setCurrentAdaptiveDifficulty] = useState('simple');
    const [consecutiveCorrect, setConsecutiveCorrect] = useState(0);
    const [masteryData, setMasteryData] = useState({ practice_mastery: 0, practice_correct: 0, practice_attempts: 0, is_eligible: false });
    const [gameStartTime, setGameStartTime] = useState(null);
    const [timerDisplay, setTimerDisplay] = useState('00:00');
    const [errorMessage, setErrorMessage] = useState('');
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [loadingText, setLoadingText] = useState('Preparing questions...');
    const [currentMainConcept, setCurrentMainConcept] = useState('');
    const [currentSubconcept, setCurrentSubconcept] = useState('');
    const [thirdAttemptUsed, setThirdAttemptUsed] = useState(false);
    const [afterMathBuddyHelp, setAfterMathBuddyHelp] = useState(false);
    const [userGrade, setUserGrade] = useState(6);
    const [isTypingResponse, setIsTypingResponse] = useState(false);
    const [showScorePopup, setShowScorePopup] = useState(null);
    const [streak, setStreak] = useState(0);
    const [showStreakBanner, setShowStreakBanner] = useState(false);
    const [particles, setParticles] = useState([]);
    const [showWorkbookGenerator, setShowWorkbookGenerator] = useState(false);
    const [levelStats, setLevelStats] = useState({
        simple: { currentLevel: 1, highestLevel: 1, totalScore: 0, totalQuestions: 0 },
        medium: { currentLevel: 1, highestLevel: 1, totalScore: 0, totalQuestions: 0 },
        hard: { currentLevel: 1, highestLevel: 1, totalScore: 0, totalQuestions: 0 },
        adaptive: { currentLevel: 1, highestLevel: 1, totalScore: 0, totalQuestions: 0 }
    });
    const [showLevelCompletePopup, setShowLevelCompletePopup] = useState(false);
    const [completedLevelNumber, setCompletedLevelNumber] = useState(0);

    const timerRef = useRef(null);
    const chatMessagesEndRef = useRef(null);
    const synth = useRef(null);
    const isSpeaking = useRef(false);
    const isMovingToNextQuestion = useRef(false);
    const isSubmitting = useRef(false);
    const speechTimeoutRef = useRef(null);

    const showAvatarSpeech = useCallback((text, expression = 'normal') => {
        setAvatarSpeech(text);
        setAvatarExpression(expression);
        setIsSpeakingAvatar(true);
        if (!voiceEnabled) {
            if (speechTimeoutRef.current) clearTimeout(speechTimeoutRef.current);
            speechTimeoutRef.current = setTimeout(() => {
                setAvatarSpeech('');
                setIsSpeakingAvatar(false);
                setAvatarExpression('normal');
            }, Math.max(2500, text.length * 45));
        }
    }, [voiceEnabled]);

    const speakText = useCallback(async (text, expression = 'normal') => {
        if (!voiceEnabled || !synth.current) {
            showAvatarSpeech(text, expression);
            return;
        }
        synth.current.cancel();
        const voices = await new Promise((resolve) => {
            const v = synth.current.getVoices();
            if (v.length > 0) resolve(v);
            else synth.current.onvoiceschanged = () => resolve(synth.current.getVoices());
        });
        const utterance = new SpeechSynthesisUtterance(text);
        const femaleVoice =
            voices.find(v => v.name === 'Google UK English Female') ||
            voices.find(v => v.name === 'Samantha') ||
            voices.find(v => v.name.includes('Microsoft Zira')) ||
            voices.find(v => v.lang === 'en-US') || null;
        if (femaleVoice) utterance.voice = femaleVoice;
        utterance.rate = 1.08;
        utterance.pitch = 1.45;
        utterance.volume = 1;
        utterance.lang = 'en-US';
        utterance.onstart = () => { isSpeaking.current = true; showAvatarSpeech(text, expression); };
        utterance.onend = () => {
            isSpeaking.current = false;
            setAvatarSpeech('');
            setIsSpeakingAvatar(false);
            setAvatarExpression('normal');
        };
        synth.current.speak(utterance);
    }, [voiceEnabled, showAvatarSpeech]);

    const spawnParticles = useCallback((type = 'correct') => {
        const colors = type === 'correct' ? ['#FFD700', '#00FF88', '#00CFFF', '#FF6BFF'] : ['#FF4D4D', '#FF8C00'];
        const newParticles = Array.from({ length: type === 'correct' ? 20 : 8 }, (_, i) => ({
            id: Date.now() + i,
            x: Math.random() * 100,
            y: Math.random() * 60 + 20,
            color: colors[Math.floor(Math.random() * colors.length)],
            size: Math.random() * 12 + 6,
            duration: Math.random() * 1000 + 800,
            shape: type === 'correct' ? ['⭐', '💫', '✨', '🎯', '💎'][Math.floor(Math.random() * 5)] : ['💔', '❌'][Math.floor(Math.random() * 2)]
        }));
        setParticles(p => [...p, ...newParticles]);
        setTimeout(() => setParticles(p => p.filter(par => !newParticles.find(n => n.id === par.id))), 1500);
    }, []);

    const startGameTimer = useCallback(() => {
        if (timerRef.current) clearInterval(timerRef.current);
        const startTime = Date.now();
        setGameStartTime(startTime);
        timerRef.current = setInterval(() => {
            const elapsed = Math.floor((Date.now() - startTime) / 1000);
            const minutes = Math.floor(elapsed / 60);
            const seconds = elapsed % 60;
            setTimerDisplay(`${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
        }, 1000);
    }, []);

    const savePracticeSession = useCallback(async (sessionData) => {
        try {
            await fetch('/api/save-practice-session', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(sessionData)
            });
        } catch (error) {
            console.error('Error saving practice session:', error);
        }
    }, []);

    const saveSubconceptPracticeSession = useCallback(async (sessionData) => {
        try {
            const dataToSend = {
                main_concept: sessionData.main_concept || currentMainConcept,
                subconcept: sessionData.subconcept || currentSubconcept,
                level: sessionData.level || currentLevel,
                level_number: sessionData.level_number || currentLevelNumber,
                difficulty: sessionData.difficulty || currentDifficulty,
                sub_level: sessionData.sub_level || currentSubLevel,
                total_questions: sessionData.total_questions || currentQuestionIndex,
                correct_answers: sessionData.correct_answers || subLevelCorrectAnswers,
                score: sessionData.score || totalScore,
                lives_remaining: sessionData.lives_remaining || lives,
                duration_seconds: sessionData.duration_seconds || 0,
                is_completed: sessionData.is_completed || false
            };
            const response = await fetch('/api/save-subconcept-practice', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(dataToSend)
            });
            const result = await response.json();
            if (result.success) {
                setMasteryData({
                    practice_mastery: result.practice_mastery,
                    practice_accuracy: result.practice_accuracy,
                    practice_sessions: result.practice_sessions,
                    practice_attempts: result.practice_questions_attempted,
                    practice_correct: result.practice_questions_correct,
                    is_eligible: result.is_practice_completed
                });
            }
            return result;
        } catch (error) {
            return { success: false };
        }
    }, [currentMainConcept, currentSubconcept, currentLevel, currentLevelNumber, currentDifficulty, currentSubLevel, currentQuestionIndex, subLevelCorrectAnswers, totalScore, lives]);

    const completeLevelAndAdvance = useCallback(async () => {
        setLevelStats(prev => ({
            ...prev,
            [currentLevel]: {
                currentLevel: currentLevelNumber + 1,
                highestLevel: Math.max(prev[currentLevel].highestLevel, currentLevelNumber + 1),
                totalScore: prev[currentLevel].totalScore + totalScore,
                totalQuestions: prev[currentLevel].totalQuestions + subLevelTotalQuestions
            }
        }));
        setCompletedLevelNumber(currentLevelNumber);
        setShowLevelCompletePopup(true);
        speakText(`Level ${currentLevelNumber} Complete! Moving to Level ${currentLevelNumber + 1}!`, 'excited');
        setTimeout(() => { setShowLevelCompletePopup(false); }, 3000);
        setCurrentLevelNumber(prev => prev + 1);
        setCurrentQuestionIndex(0);
        setTotalScore(0);
        setLives(3);
        setSelectedAnswer(null);
        setQuestionAnswered(false);
        setCurrentAttempt(0);
        setSubLevelCorrectAnswers(0);
        setSubLevelTotalQuestions(0);
        setConsecutiveCorrect(0);
        setStreak(0);
        const newDifficulty = Math.min(currentDifficulty + 1, currentLevel === 'simple' ? 3 : currentLevel === 'medium' ? 6 : 8);
        setCurrentDifficulty(newDifficulty);
        await generateLevelQuestions(true);
    }, [currentLevel, currentLevelNumber, currentDifficulty, subLevelTotalQuestions, subLevelCorrectAnswers, totalScore, speakText]);

    const endGame = useCallback(async (completed) => {
        if (timerRef.current) clearInterval(timerRef.current);
        const sessionDuration = gameStartTime ? Math.floor((Date.now() - gameStartTime) / 1000) : 0;
        if (completed && currentQuestionIndex >= currentQuestions.length - 1 && currentQuestions.length > 0) {
            await completeLevelAndAdvance();
        } else {
            await saveSubconceptPracticeSession({ total_questions: currentQuestionIndex, correct_answers: subLevelCorrectAnswers, duration_seconds: sessionDuration, is_completed: completed });
            await savePracticeSession({ level: currentLevel, level_number: currentLevelNumber, difficulty: currentDifficulty, sub_level: currentSubLevel, total_questions: currentQuestionIndex, correct_answers: subLevelCorrectAnswers, score: totalScore, lives_remaining: lives, duration_seconds: sessionDuration, is_completed: completed, concepts: [currentMainConcept] });
            setGameState('gameOver');
            setShowAvatar(false);
            setChatbotActive(false);
            speakText(completed ? 'Amazing! Session complete! You are a math champion!' : 'Game over! Every mistake is a lesson!', completed ? 'happy' : 'sad');
        }
    }, [gameStartTime, currentQuestionIndex, currentQuestions.length, subLevelCorrectAnswers, currentLevel, currentLevelNumber, currentDifficulty, currentSubLevel, totalScore, lives, currentMainConcept, saveSubconceptPracticeSession, savePracticeSession, completeLevelAndAdvance, speakText]);

    const submitPracticeAnswerToBackend = useCallback(async (question, isCorrect, attemptNumber) => {
        try {
            const response = await fetch('/api/submit-practice-answer', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    concept: question.concept || currentMainConcept || 'Mathematics',
                    subconcept: question.subconcept || currentSubconcept || 'General Practice',
                    is_correct: isCorrect,
                    difficulty: currentLevel,
                    level_number: currentLevelNumber,
                    attempt_number: attemptNumber || 1
                })
            });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const data = await response.json();
            if (data.success && data.mastery) {
                setMasteryData({
                    practice_mastery: data.mastery.practice_mastery,
                    practice_accuracy: data.mastery.practice_accuracy,
                    practice_attempts: data.mastery.practice_attempts,
                    practice_correct: data.mastery.practice_correct,
                    is_eligible: data.mastery.is_eligible || false
                });
            }
            return data;
        } catch (error) {
            return { success: false };
        }
    }, [currentMainConcept, currentSubconcept, currentLevel, currentLevelNumber]);

    const handleIncorrectAnswer = useCallback((question) => {
        setSubLevelTotalQuestions(prev => prev + 1);
        const newAttempt = currentAttempt + 1;
        setCurrentAttempt(newAttempt);
        spawnParticles('wrong');
        if (newAttempt === 1) {
            speakText('Not quite! You have 2 more chances!', 'sad');
            setSelectedAnswer(null);
            setQuestionAnswered(false);
        } else if (newAttempt === 2) {
            speakText(`Hint: ${question.hint || 'Think about the key concept!'}`, 'thinking');
            setSelectedAnswer(null);
            setQuestionAnswered(false);
        } else if (newAttempt === 3 && !thirdAttemptUsed) {
            setThirdAttemptUsed(true);
            setCurrentQuestionForHelp(question);
            setShowHelpPopup(true);
            speakText('Need AI-powered help? I can explain this!', 'excited');
            setSelectedAnswer(null);
            setQuestionAnswered(false);
        } else {
            setQuestionAnswered(true);
            setLives(prev => Math.max(0, prev - 2));
            setTotalScore(prev => Math.max(0, prev - 20));
            speakText('Study the explanation carefully!', 'sad');
            setTimeout(() => {
                if (currentQuestionIndex + 1 < currentQuestions.length) {
                    setCurrentQuestionIndex(prev => prev + 1);
                    setSelectedAnswer(null);
                    setQuestionAnswered(false);
                    setCurrentAttempt(0);
                    setThirdAttemptUsed(false);
                    setAfterMathBuddyHelp(false);
                } else {
                    endGame(true);
                }
            }, 2500);
        }
    }, [currentAttempt, thirdAttemptUsed, speakText, currentQuestionIndex, currentQuestions.length, endGame, spawnParticles]);

    const submitAnswer = useCallback(async () => {
        if (questionAnswered || selectedAnswer === null || isMovingToNextQuestion.current || isSubmitting.current) return;
        const question = currentQuestions[currentQuestionIndex];
        if (!question) return;
        isSubmitting.current = true;
        const selectedLetter = String.fromCharCode(65 + selectedAnswer);
        const correctAnswer = normalizeAnswerForComparison(question.correct_answer);
        const isCorrect = selectedLetter === correctAnswer;
        setQuestionAnswered(true);
        await submitPracticeAnswerToBackend(question, isCorrect, currentAttempt + 1);
        if (isCorrect) {
            setTotalScore(prev => {
                const pts = getPointsForLevel(currentLevel);
                setShowScorePopup(`+${pts}`);
                setTimeout(() => setShowScorePopup(null), 1200);
                return prev + pts;
            });
            setSubLevelCorrectAnswers(prev => prev + 1);
            setSubLevelTotalQuestions(prev => prev + 1);
            spawnParticles('correct');
            const newStreak = streak + 1;
            setStreak(newStreak);
            if (newStreak > 1) {
                setShowStreakBanner(true);
                setTimeout(() => setShowStreakBanner(false), 2000);
            }
            if (adaptiveMode) {
                setConsecutiveCorrect(prev => {
                    const next = prev + 1;
                    if (next >= 2 && currentAdaptiveDifficulty !== 'hard') {
                        const newDifficulty = currentAdaptiveDifficulty === 'simple' ? 'medium' : 'hard';
                        setCurrentAdaptiveDifficulty(newDifficulty);
                        setCurrentLevel(newDifficulty);
                        setCurrentDifficulty(newDifficulty === 'simple' ? 1 : newDifficulty === 'medium' ? 2 : 3);
                        return 0;
                    }
                    return next;
                });
            }
            speakText('Brilliant! Keep it up!', 'excited');
            isMovingToNextQuestion.current = true;
            const currentIdx = currentQuestionIndex;
            const hasMore = currentIdx + 1 < currentQuestions.length;
            setTimeout(() => {
                if (hasMore) {
                    setCurrentQuestionIndex(currentIdx + 1);
                    setSelectedAnswer(null);
                    setQuestionAnswered(false);
                    setCurrentAttempt(0);
                    setThirdAttemptUsed(false);
                    setAfterMathBuddyHelp(false);
                } else {
                    endGame(true);
                }
                setTimeout(() => { isMovingToNextQuestion.current = false; isSubmitting.current = false; }, 500);
            }, 1500);
        } else {
            setStreak(0);
            isSubmitting.current = false;
            handleIncorrectAnswer(question);
        }
    }, [questionAnswered, selectedAnswer, currentQuestions, currentQuestionIndex, currentLevel, adaptiveMode, currentAdaptiveDifficulty, streak, speakText, endGame, submitPracticeAnswerToBackend, currentAttempt, thirdAttemptUsed, handleIncorrectAnswer, spawnParticles]);

    const selectAnswer = (index) => {
        if (questionAnswered || isSubmitting.current) return;
        setSelectedAnswer(index);
        setTimeout(() => submitAnswer(), 100);
    };

    const generateLevelQuestions = useCallback(async (isNextLevel = false) => {
        setGameState('loading');
        setLoadingProgress(0);
        setLoadingText(isNextLevel ? `Loading Level ${currentLevelNumber + 1}...` : 'Loading questions from database...');
        const interval = setInterval(() => {
            setLoadingProgress(prev => Math.min(prev + Math.random() * 15, 90));
        }, 600);
        try {
            const urlParams = new URLSearchParams(window.location.search);
            const concept = urlParams.get('concept') || 'Mathematics';
            const subconcept = urlParams.get('subconcept') || 'General Practice';
            setCurrentMainConcept(concept);
            setCurrentSubconcept(subconcept);
            let adjustedDifficulty = currentDifficulty;
            if (currentLevelNumber > 1) {
                adjustedDifficulty = Math.min(currentDifficulty + Math.floor(currentLevelNumber / 3), 10);
            }
            const response = await fetch('/api/generate-gamified-questions', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ level: currentLevel, difficulty: adjustedDifficulty, level_number: currentLevelNumber, count: 5, concept, subconcept })
            });
            if (!response.ok) throw new Error('Failed to generate questions');
            const data = await response.json();
            if (data.success && data.questions.length) {
                setCurrentQuestions(data.questions);
                clearInterval(interval);
                setLoadingProgress(100);
                setLoadingText('Ready to launch! 🚀');
                setTimeout(() => {
                    setGameState('game');
                    if (!isNextLevel) startGameTimer();
                    speakText(isNextLevel ? `Level ${currentLevelNumber}! Let's go!` : 'Game on! Good luck, champion!', 'excited');
                }, 1000);
            } else {
                throw new Error(data.message || 'No questions generated');
            }
        } catch (error) {
            clearInterval(interval);
            setErrorMessage(error.message);
            setGameState('error');
        }
    }, [currentLevel, currentDifficulty, currentLevelNumber, startGameTimer, speakText]);

    const selectLevel = (level, difficulty) => {
        if (level === 'adaptive') {
            setAdaptiveMode(true);
            setCurrentAdaptiveDifficulty('simple');
            setCurrentLevel('adaptive');
            setCurrentDifficulty(7);
        } else {
            setAdaptiveMode(false);
            setCurrentLevel(level);
            setCurrentDifficulty(difficulty);
        }
        setCurrentLevelNumber(1);
        setCurrentSubLevel(1);
        setCurrentQuestionIndex(0);
        setTotalScore(0);
        setLives(3);
        setSelectedAnswer(null);
        setQuestionAnswered(false);
        setCurrentAttempt(0);
        setSubLevelCorrectAnswers(0);
        setSubLevelTotalQuestions(0);
        setConsecutiveCorrect(0);
        setStreak(0);
        generateLevelQuestions(false);
    };

    const quitGame = () => {
        if (window.confirm('Quit the game? Your progress will be saved.')) endGame(false);
    };

    const playAgain = () => {
        setGameState('levelSelection');
        setShowAvatar(true);
        setChatbotActive(false);
        speakText('Welcome back! Choose your level!', 'happy');
    };

    const goBack = () => {
        if (gameStartTime && currentQuestionIndex > 0) {
            if (window.confirm('Save progress and exit?')) { endGame(false); window.location.href = '/dashboard'; }
        } else { window.location.href = '/dashboard'; }
    };

    const addMessage = useCallback((type, content) => {
        setChatMessages(prev => [...prev, { type, content, id: Date.now() + Math.random() }]);
    }, []);

    const sendChatMessage = async () => {
        if (!chatInput.trim() || isTypingResponse) return;
        const userMessage = chatInput;
        addMessage('user', userMessage);
        setChatInput('');
        const question = currentQuestions[currentQuestionIndex];
        let optionsText = '';
        if (question?.options) {
            if (Array.isArray(question.options)) optionsText = question.options.join(', ');
            else if (typeof question.options === 'object') optionsText = Object.values(question.options).join(', ');
        }
        setIsTyping(true);
        try {
            const response = await fetch('/api/ai-chat-response', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ message: userMessage, question_context: `Question: ${question?.question || 'No question'}\nOptions: ${optionsText}\nConcept: ${question?.concept || 'General'}\nGrade: ${userGrade}`, user_grade: userGrade })
            });
            const data = await response.json();
            setIsTyping(false);
            if (data.success) {
                addMessage('bot', data.response);
                speakText('I hope that helps!', 'happy');
            } else {
                addMessage('bot', "Oops! I'm having trouble. Try asking differently!");
            }
        } catch {
            setIsTyping(false);
            addMessage('bot', "Sorry, couldn't process that. Please try again!");
        }
    };

    const addUnderstandingButtons = useCallback(() => {
        addMessage('bot_understand', null);
    }, [addMessage]);

    const getPromptForOption = (optionType, question) => {
        let optionsText = '';
        if (Array.isArray(question.options)) optionsText = question.options.join(', ');
        else if (typeof question.options === 'object') optionsText = Object.values(question.options).join(', ');
        const base = `Question: "${question.question}". Options: ${optionsText}. Concept: ${question.concept}. Grade: ${userGrade}`;
        const prompts = {
            simple: `Explain this math question in very simple, friendly language for a ${userGrade}th grade student. Use easy words and fun examples. Keep it short and clear. ${base}`,
            step_by_step: `Explain this math question step by step for a ${userGrade}th grade student. Break it down into 3-4 clear numbered steps. ${base}`,
            with_example: `Explain this math question with a fun real-life example for a ${userGrade}th grade student. Create a simple, relatable story. ${base}`,
            detailed: `Give a detailed but easy explanation of this math question for a ${userGrade}th grade student. Include the key concepts and reasoning. ${base}`
        };
        return prompts[optionType] || prompts.simple;
    };

    const addExplanationOptions = useCallback((question) => {
        const options = [
            { type: 'simple', emoji: '🧩', text: 'Explain Simply' },
            { type: 'step_by_step', emoji: '📝', text: 'Step by Step' },
            { type: 'with_example', emoji: '🎯', text: 'With Example' },
            { type: 'detailed', emoji: '🔍', text: 'In Detail' },
            { type: 'video', emoji: '🎥', text: 'Video Explanation' }
        ];
        addMessage('bot_options', { options, question });
    }, [addMessage]);

    const handleExplanationOption = useCallback(async (optionType, question) => {
        if (optionType === 'video') {
            addMessage('user', '🎥 Watch Video Explanation');
            setIsTyping(true);
            try {
                const response = await fetch('/api/get-concept-video', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ concept: question.concept || 'General Math', subconcept: question.subconcept || 'Basic Concepts', question: question.question || '', grade: userGrade })
                });
                const data = await response.json();
                setIsTyping(false);
                if (data.success && data.video_url) {
                    setVideoModal({ open: true, url: data.video_url, title: data.video_title || 'Video Explanation' });
                    addMessage('bot', `🎥 Found a video explanation for "${data.video_title || 'this topic'}"! Check out the popup window to watch it. 📺`);
                    speakText('I found a video explanation for you! Check the popup window.', 'excited');
                } else {
                    addMessage('bot', "❌ Couldn't find a video. Let me explain in detail instead!");
                    setTimeout(() => handleExplanationOption('detailed', question), 1000);
                }
            } catch {
                setIsTyping(false);
                addMessage('bot', "❌ Video unavailable. Here's a detailed explanation instead!");
                setTimeout(() => handleExplanationOption('detailed', question), 1000);
            }
            return;
        }
        const optionLabels = { simple: '🧩 Explain Simply', step_by_step: '📝 Step by Step', with_example: '🎯 With Example', detailed: '🔍 In Detail' };
        addMessage('user', optionLabels[optionType] || 'Explain');
        setIsTyping(true);
        speakText('Let me think about that...', 'thinking');
        try {
            const response = await fetch('/api/ai-chat-response', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ message: getPromptForOption(optionType, question), question_context: `Question: ${question.question}\nConcept: ${question.concept}\nType: ${optionType}`, user_grade: userGrade })
            });
            const data = await response.json();
            setIsTyping(false);
            if (data.success && data.response) {
                addMessage('bot', data.response);
                speakText('Hope that makes sense!', 'happy');
                setTimeout(() => addUnderstandingButtons(), 800);
            } else {
                addMessage('bot', "Let me help you break this down step by step!");
                setTimeout(() => addUnderstandingButtons(), 800);
            }
        } catch {
            setIsTyping(false);
            addMessage('bot', "I'm here to help! Let's think through this together...");
            setTimeout(() => addUnderstandingButtons(), 800);
        }
    }, [addMessage, userGrade, speakText, addUnderstandingButtons]);

    const acceptMathBuddyHelp = () => {
        const currentQ = currentQuestions[currentQuestionIndex];
        if (!currentQ) { setShowHelpPopup(false); return; }
        setCurrentQuestionForHelp(currentQ);
        setShowHelpPopup(false);
        setTimeout(() => {
            setChatbotActive(true);
            setChatMessages([{ type: 'bot', content: `Hi! I'm SolveSmart! 🎓 I see you're stuck. Let me help you understand this!`, id: Date.now() }]);
            speakText("Hi! Let me help you understand this question!", 'excited');
            setTimeout(() => addExplanationOptions(currentQ), 600);
        }, 100);
    };

    const declineMathBuddyHelp = () => {
        const currentQ = currentQuestions[currentQuestionIndex];
        setShowHelpPopup(false);
        if (currentQ?.hint) { speakText(`Hint: ${currentQ.hint}`, 'thinking'); }
        else { speakText("Think carefully about the concept! You can do it!", 'happy'); }
        setSelectedAnswer(null);
        setQuestionAnswered(false);
        setAfterMathBuddyHelp(true);
    };

    const closeChatbot = () => { setChatbotActive(false); };

    const initializeUserGrade = useCallback(async () => {
        try {
            const response = await fetch('/api/user-info');
            const data = await response.json();
            if (data.success && data.grade) setUserGrade(parseInt(data.grade) || 6);
        } catch { }
    }, []);

    const isPrimaryGrade = useCallback(() => userGrade <= 5, [userGrade]);

    useEffect(() => {
        if (window.speechSynthesis) {
            synth.current = window.speechSynthesis;
            window.speechSynthesis.onvoiceschanged = () => { synth.current.getVoices(); };
        }
        initializeUserGrade();
        const urlParams = new URLSearchParams(window.location.search);
        const concept = urlParams.get('concept');
        const subconcept = urlParams.get('subconcept');
        if (concept && subconcept) { setCurrentMainConcept(concept); setCurrentSubconcept(subconcept); }
        setTimeout(() => speakText('Welcome! Choose your challenge level!', 'happy'), 500);
    }, [initializeUserGrade]);

    useEffect(() => {
        if (chatMessagesEndRef.current) chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }, [chatMessages, isTyping]);

    useEffect(() => {
        if (lives <= 0 && gameState === 'game') endGame(false);
    }, [lives, gameState, endGame]);

    useEffect(() => {
        const handleUnderstood = () => {
            addMessage('user', '✅ Yes, I understand!');
            setTimeout(() => {
                addMessage('bot', '🎉 Awesome! Now try the question again. You got this! 💪');
                setTimeout(() => {
                    closeChatbot();
                    setSelectedAnswer(null);
                    setQuestionAnswered(false);
                    setCurrentAttempt(0);
                    setThirdAttemptUsed(false);
                    setAfterMathBuddyHelp(true);
                    speakText("Now try it! You know this!", 'excited');
                }, 1500);
            }, 400);
        };
        const handleExplainMore = () => {
            addMessage('user', '🔄 Explain more please');
            setIsTyping(true);
            const question = currentQuestions[currentQuestionIndex];
            fetch('/api/ai-chat-response', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ message: `Explain this concept in more detail with another real-life example: ${question?.concept}. Question: ${question?.question}`, question_context: `Need more explanation. Concept: ${question?.concept}`, user_grade: userGrade })
            })
                .then(r => r.json())
                .then(data => {
                    setIsTyping(false);
                    addMessage('bot', data.success ? data.response : "Let me try explaining differently!");
                    setTimeout(() => addUnderstandingButtons(), 800);
                })
                .catch(() => { setIsTyping(false); addMessage('bot', "Practice makes perfect! Try similar questions!"); setTimeout(() => addUnderstandingButtons(), 800); });
        };
        window.addEventListener('understood', handleUnderstood);
        window.addEventListener('explainMore', handleExplainMore);
        return () => { window.removeEventListener('understood', handleUnderstood); window.removeEventListener('explainMore', handleExplainMore); };
    }, [currentQuestions, currentQuestionIndex, userGrade, addMessage, addUnderstandingButtons, speakText]);

    // Render: Level Selection
    const renderLevelSelection = () => (
        <div className="gp-level-selection">
            <div className="gp-level-header">
                <div className="gp-level-header-bg"></div>
                <div className="gp-level-header-content">
                    <div className="gp-title-badge">Math Quest</div>
                    <h1 className="gp-main-title">Choose Your Challenge</h1>
                    <p className="gp-subtitle">Infinite levels await! Each level has 5 questions. Keep going to reach higher levels!</p>
                </div>
            </div>
            <div className="gp-level-grid">
                <div className="gp-level-card adaptive" onClick={() => selectLevel('adaptive', 7)}>
                    <div className="level-card-glow"></div>
                    <div className="level-card-icon">🎯</div>
                    <div className="level-card-badge">SMART</div>
                    <h3>Adaptive Mode</h3>
                    <p>AI adjusts difficulty based on your performance in real-time</p>
                    <div className="level-card-pts">Dynamic Points</div>
                    <div className="level-stats">Level {levelStats.adaptive.highestLevel} reached</div>
                </div>
                <div className="gp-level-card simple" onClick={() => selectLevel('simple', 1)}>
                    <div className="level-card-glow"></div>
                    <div className="level-card-icon">🌱</div>
                    <h3>Simple</h3>
                    <p>Perfect for beginners. Infinite levels, increasing challenge!</p>
                    <div className="level-card-pts">+10 pts / question</div>
                    <div className="level-stats">🏆 Level {levelStats.simple.highestLevel} | Score: {levelStats.simple.totalScore}</div>
                </div>
                <div className="gp-level-card medium" onClick={() => selectLevel('medium', 4)}>
                    <div className="level-card-glow"></div>
                    <div className="level-card-icon">🔥</div>
                    <div className="level-card-badge">POPULAR</div>
                    <h3>Medium</h3>
                    <p>Real challenge. Push your limits further, level by level!</p>
                    <div className="level-card-pts">+40 pts / question</div>
                    <div className="level-stats">🏆 Level {levelStats.medium.highestLevel} | Score: {levelStats.medium.totalScore}</div>
                </div>
                <div className="gp-level-card hard" onClick={() => selectLevel('hard', 5)}>
                    <div className="level-card-glow"></div>
                    <div className="level-card-icon">⚡</div>
                    <h3>Hard</h3>
                    <p>For the brave ones. Maximum challenge with infinite progression!</p>
                    <div className="level-card-pts">+50 pts / question</div>
                    <div className="level-stats">🏆 Level {levelStats.hard.highestLevel} | Score: {levelStats.hard.totalScore}</div>
                </div>
            </div>
            <div className="gp-workbook-section">
                <button
                    className="gp-workbook-btn"
                    onClick={() => {
                        if (currentQuestions && currentQuestions.length > 0) {
                            setShowWorkbookGenerator(true);
                        } else {
                            alert('Please start a practice session first to generate a workbook from your questions!');
                        }
                    }}
                >
                    📚 Generate Workbook from Current Questions
                </button>
                <p className="workbook-hint">Create a printable PDF worksheet with answers and explanations from your current practice session</p>
            </div>
            <div className="gp-back-btn-wrap">
                <button className="gp-back-btn" onClick={goBack}>← Back to Results</button>
            </div>
        </div>
    );

    // Render: Level Complete Popup
    const renderLevelCompletePopup = () => {
        if (!showLevelCompletePopup) return null;
        return (
            <div className="gp-level-complete-overlay">
                <div className="gp-level-complete-popup">
                    <div className="popup-emoji">🎉🏆🎉</div>
                    <h2>Level {completedLevelNumber} Complete!</h2>
                    <p>You've mastered Level {completedLevelNumber}!</p>
                    <div className="popup-unlock">✨ Moving to <strong>Level {completedLevelNumber + 1}</strong>! ✨</div>
                    <div className="popup-stats">
                        <div>Score: <strong>{totalScore}</strong></div>
                        <div>Accuracy: <strong>{subLevelTotalQuestions > 0 ? Math.round((subLevelCorrectAnswers / subLevelTotalQuestions) * 100) : 0}%</strong></div>
                    </div>
                    <div className="popup-loading">Loading next level...</div>
                </div>
            </div>
        );
    };

    // Render: Loading
    const renderLoading = () => (
        <div className="gp-loading">
            <div className="gp-loading-orb"></div>
            <div className="gp-loading-content">
                <div className="gp-loading-robot">🤖</div>
                <h2>Generating Your Challenge</h2>
                <p>AI is crafting unique questions just for you...</p>
                <div className="gp-progress-bar">
                    <div className="gp-progress-fill" style={{ width: `${loadingProgress}%` }}></div>
                </div>
                <div className="gp-loading-text">{loadingText}</div>
                <div className="gp-loading-dots"><span></span><span></span><span></span></div>
            </div>
        </div>
    );

    // Render: Error
    const renderError = () => (
        <div className="gp-error">
            <div className="gp-error-icon">⚠️</div>
            <h2>Oops! Something went wrong</h2>
            <p>{errorMessage}</p>
            <div className="gp-error-tips">
                <p>Try these fixes:</p>
                <ul>
                    <li>Check your internet connection</li>
                    <li>Verify your API key has credits</li>
                    <li>Refresh and try again</li>
                </ul>
            </div>
            <div className="gp-error-btns">
                <button className="gp-btn-primary" onClick={generateLevelQuestions}>🔄 Try Again</button>
                <button className="gp-btn-ghost" onClick={() => setGameState('levelSelection')}>← Choose Level</button>
            </div>
        </div>
    );

    // Render: Game
    const renderGame = () => {
        const question = currentQuestions[currentQuestionIndex];
        if (!question) return (
            <div className="gp-error">
                <h4>Error: No question found</h4>
                <button className="gp-btn-primary" onClick={() => setGameState('levelSelection')}>Back</button>
            </div>
        );
        let options = [];
        if (Array.isArray(question.options)) options = question.options.slice(0, 4);
        else if (typeof question.options === 'object' && question.options !== null) {
            options = ['A', 'B', 'C', 'D'].map(k => question.options[k] || '');
        }
        const progress = ((currentQuestionIndex + 1) / currentQuestions.length) * 100;
        const optionLetters = ['A', 'B', 'C', 'D'];
        const optionColors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4'];
        return (
            <div className="gp-game-wrap">
                <div className="gp-hud">
                    <div className="gp-hud-left">
                        <div className="gp-hud-level">
                            <span className="hud-label">LEVEL</span>
                            <span className="hud-value">{capitalizeFirst(adaptiveMode ? currentAdaptiveDifficulty : currentLevel)} {currentLevelNumber}</span>
                        </div>
                        <div className="gp-hud-score">
                            <span className="hud-label">SCORE</span>
                            <span className="hud-value score-value">{totalScore}</span>
                            {showScorePopup && <div className="score-popup">{showScorePopup}</div>}
                        </div>
                    </div>
                    <div className="gp-hud-center">
                        <div className="gp-progress-track">
                            <div className="gp-progress-fill" style={{ width: `${progress}%` }}></div>
                        </div>
                        <span className="gp-q-counter">Q {currentQuestionIndex + 1} / {currentQuestions.length}</span>
                    </div>
                    <div className="gp-hud-right">
                        <div className="gp-hud-lives">
                            {[...Array(3)].map((_, i) => (
                                <span key={i} className={`life-heart ${i < lives ? 'alive' : 'dead'}`}>❤️</span>
                            ))}
                        </div>
                        <div className="gp-hud-timer">
                            <span className="hud-label">⏱</span>
                            <span className="hud-value">{timerDisplay}</span>
                        </div>
                        <button className="gp-quit-btn" onClick={quitGame} title="Quit">✕</button>
                    </div>
                </div>
                {showStreakBanner && (<div className="gp-streak-banner">🔥 {streak} Streak! Amazing!</div>)}
                <div className="gp-level-badge">
                    Level {currentLevelNumber} • {capitalizeFirst(currentLevel)} Difficulty
                    {currentLevelNumber > 1 && <span className="level-progress"> ⚡ Getting harder!</span>}
                </div>
                <div className="gp-mastery-strip">
                    <span className="mastery-label">Mastery</span>
                    <div className="mastery-track">
                        <div className="mastery-fill" style={{ width: `${Math.min(100, masteryData.practice_mastery)}%` }}></div>
                    </div>
                    <span className="mastery-pct">{masteryData.practice_mastery}%</span>
                    <span className={`mastery-status ${masteryData.practice_correct >= 25 ? 'ready' : ''}`}>
                        {masteryData.practice_correct >= 25 ? '✓ Assessment Ready!' : `${25 - masteryData.practice_correct} more to unlock`}
                    </span>
                </div>
                <div className={`gp-question-card ${questionAnswered ? 'answered' : ''}`}>
                    <div className="gp-q-meta">
                        <span className="gp-q-num">Question {currentQuestionIndex + 1}</span>
                        {question.was_validated && <span className="gp-validated-badge">✅ AI Validated</span>}
                        <span className="gp-pts-badge">+{getPointsForLevel(currentLevel)} pts</span>
                    </div>
                    <div className="gp-q-concept">{question.concept}</div>
                    <div className="gp-q-text">{question.question}</div>
                    <div className="gp-options">
                        {options.map((opt, idx) => (
                            <button
                                key={idx}
                                className={`gp-option ${selectedAnswer === idx ? 'selected' : ''}`}
                                style={{ '--option-color': optionColors[idx] }}
                                onClick={() => selectAnswer(idx)}
                                disabled={questionAnswered || isSubmitting.current}
                            >
                                <span className="gp-opt-letter">{optionLetters[idx]}</span>
                                <span className="gp-opt-text">{opt || `Option ${optionLetters[idx]}`}</span>
                            </button>
                        ))}
                    </div>
                    {currentAttempt > 0 && (
                        <div className="gp-attempts">
                            {[...Array(3)].map((_, i) => (
                                <div key={i} className={`attempt-dot ${i < currentAttempt ? 'used' : ''}`}></div>
                            ))}
                            <span className="attempts-label">Attempt {currentAttempt}/3</span>
                        </div>
                    )}
                </div>
                <div className="gp-controls">
                    <button className={`gp-voice-btn ${voiceEnabled ? 'on' : 'off'}`} onClick={() => setVoiceEnabled(!voiceEnabled)}>
                        {voiceEnabled ? '🔊 Voice ON' : '🔇 Voice OFF'}
                    </button>
                    <button className="gp-help-btn" onClick={() => {
                        const currentQ = currentQuestions[currentQuestionIndex];
                        if (currentQ) { setChatbotActive(true); setTimeout(() => addExplanationOptions(currentQ), 300); }
                    }}>
                        💡 Ask SolveSmart
                    </button>
                </div>
            </div>
        );
    };

    // Render: Game Over
    const renderGameOver = () => {
        const accuracy = subLevelTotalQuestions > 0 ? Math.round((subLevelCorrectAnswers / subLevelTotalQuestions) * 100) : 0;
        const grade = accuracy >= 80 ? '🏆 Champion!' : accuracy >= 60 ? '⭐ Great Job!' : accuracy >= 40 ? '💪 Good Effort!' : '📚 Keep Practicing!';
        return (
            <div className="gp-gameover">
                <div className="gameover-confetti">
                    {[...Array(20)].map((_, i) => (
                        <div key={i} className="confetti-piece" style={{ left: `${Math.random() * 100}%`, animationDelay: `${Math.random() * 2}s`, background: ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4'][i % 5] }}></div>
                    ))}
                </div>
                <div className="gameover-content">
                    <div className="gameover-emoji">🎉</div>
                    <h2>Session Complete!</h2>
                    <div className="gameover-grade">{grade}</div>
                    <div className="gameover-stats">
                        <div className="stat-box"><div className="stat-number" style={{ color: '#FFD700' }}>{totalScore}</div><div className="stat-label">Total Score</div></div>
                        <div className="stat-box"><div className="stat-number" style={{ color: '#4ECDC4' }}>{currentQuestionIndex}</div><div className="stat-label">Questions</div></div>
                        <div className="stat-box"><div className="stat-number" style={{ color: '#45B7D1' }}>{accuracy}%</div><div className="stat-label">Accuracy</div></div>
                        <div className="stat-box"><div className="stat-number" style={{ color: '#96CEB4' }}>{lives}</div><div className="stat-label">Lives Left</div></div>
                    </div>
                    <div className="gameover-btns">
                        <button className="gp-btn-primary" onClick={playAgain}>🔄 Play Again</button>
                        <button className="gp-btn-ghost" onClick={goBack}>🏠 Back to Results</button>
                    </div>
                </div>
            </div>
        );
    };

    // Render: Chatbot
    const renderChatbot = () => (
        <div className="gp-chatbot">
            <div className="chatbot-header">
                <div className="chatbot-header-info">
                    <div className="chatbot-avatar-mini">🤖</div>
                    <div>
                        <div className="chatbot-name">SolveSmart AI</div>
                        <div className="chatbot-status">● Online — Ready to help!</div>
                    </div>
                </div>
                <button className="chatbot-close" onClick={closeChatbot}>✕</button>
            </div>
            <div className="chatbot-messages" id="chatMessagesContainer">
                {chatMessages.map((msg) => {
                    if (msg.type === 'bot_options') {
                        return (
                            <div key={msg.id} className="chat-msg bot-msg">
                                <div className="msg-avatar">🤖</div>
                                <div className="msg-bubble bot-bubble">
                                    <p className="options-header">How would you like me to explain? 🤔</p>
                                    <div className="explanation-options-grid">
                                        {msg.content.options.map(opt => (
                                            <button key={opt.type} className="explain-option-btn" onClick={() => handleExplanationOption(opt.type, msg.content.question)}>
                                                <span className="explain-opt-emoji">{opt.emoji}</span>
                                                <span className="explain-opt-text">{opt.text}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        );
                    }
                    if (msg.type === 'bot_understand') {
                        return (
                            <div key={msg.id} className="chat-msg bot-msg">
                                <div className="msg-avatar">🤖</div>
                                <div className="msg-bubble bot-bubble">
                                    <p>Did this explanation help? 😊</p>
                                    <div className="understand-btns">
                                        <button className="understand-yes" onClick={() => window.dispatchEvent(new CustomEvent('understood'))}>✅ Yes, I Understand!</button>
                                        <button className="understand-more" onClick={() => window.dispatchEvent(new CustomEvent('explainMore'))}>🔄 Explain More</button>
                                    </div>
                                </div>
                            </div>
                        );
                    }
                    return (
                        <div key={msg.id} className={`chat-msg ${msg.type === 'user' ? 'user-msg' : 'bot-msg'}`}>
                            <div className="msg-avatar">{msg.type === 'user' ? '👤' : '🤖'}</div>
                            <div className={`msg-bubble ${msg.type === 'user' ? 'user-bubble' : 'bot-bubble'}`}>
                                {typeof msg.content === 'string' ? <div dangerouslySetInnerHTML={{ __html: msg.content }} /> : <p>{msg.content}</p>}
                            </div>
                        </div>
                    );
                })}
                {isTyping && (
                    <div className="chat-msg bot-msg">
                        <div className="msg-avatar">🤖</div>
                        <div className="msg-bubble bot-bubble">
                            <div className="typing-dots"><span></span><span></span><span></span></div>
                        </div>
                    </div>
                )}
                <div ref={chatMessagesEndRef} />
            </div>
            <div className="chatbot-input-area">
                <input type="text" className="chatbot-input" placeholder="Ask me anything about math..." value={chatInput} onChange={(e) => setChatInput(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()} />
                <button className="chatbot-send" onClick={sendChatMessage}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>
                </button>
            </div>
        </div>
    );

    // Render: Help Popup
    const renderHelpPopup = () => {
        if (!showHelpPopup) return null;
        return (
            <div className="gp-help-overlay" onClick={() => setShowHelpPopup(false)}>
                <div className="gp-help-popup" onClick={e => e.stopPropagation()}>
                    <div className="help-popup-robot">🤖</div>
                    <div className="help-popup-glow"></div>
                    <h3>Need My Help? 🤔</h3>
                    <p>I can explain this question step by step and help you truly understand the concept!</p>
                    <div className="help-popup-btns">
                        <button className="help-yes-btn" onClick={acceptMathBuddyHelp}>✨ Yes, Help Me!</button>
                        <button className="help-no-btn" onClick={declineMathBuddyHelp}>💪 I'll Try Again</button>
                    </div>
                </div>
            </div>
        );
    };

    // Main Render
    return (
        <div className={`gp-root ${isPrimaryGrade() ? 'primary-grade' : ''}`}>
            <div className="gp-bg">
                <div className="bg-orb orb1"></div>
                <div className="bg-orb orb2"></div>
                <div className="bg-orb orb3"></div>
                <div className="bg-grid"></div>
            </div>
            <div className="gp-particles">
                {particles.map(p => (
                    <div key={p.id} className="gp-particle" style={{ left: `${p.x}%`, top: `${p.y}%`, fontSize: `${p.size}px`, animationDuration: `${p.duration}ms` }}>{p.shape}</div>
                ))}
            </div>
            <div className={`gp-layout ${chatbotActive ? 'with-chatbot' : ''}`}>
                <div className="gp-main">
                    {gameState === 'levelSelection' && renderLevelSelection()}
                    {gameState === 'loading' && renderLoading()}
                    {gameState === 'error' && renderError()}
                    {gameState === 'game' && renderGame()}
                    {gameState === 'gameOver' && renderGameOver()}
                </div>
                {chatbotActive && (<div className="gp-chatbot-col">{renderChatbot()}</div>)}
            </div>

            <MathBuddyAvatar isSpeaking={isSpeakingAvatar} expression={avatarExpression} speechText={avatarSpeech} visible={showAvatar} onClick={() => speakText("Hi! Need help? I'm here!", 'happy')} />

            {renderHelpPopup()}
            {renderLevelCompletePopup()}

            {showWorkbookGenerator && (
                <WorkbookGenerator
                    isOpen={showWorkbookGenerator}
                    onClose={() => setShowWorkbookGenerator(false)}
                    questions={currentQuestions}
                    totalScore={totalScore}
                    accuracy={subLevelTotalQuestions > 0 ? Math.round((subLevelCorrectAnswers / subLevelTotalQuestions) * 100) : 0}
                    concept={currentMainConcept}
                    subconcept={currentSubconcept}
                    difficulty={currentLevel}
                    levelNumber={currentLevelNumber}
                    userGrade={userGrade}
                />
            )}

            {videoModal.open && (
                <div className="video-modal-overlay" onClick={() => setVideoModal({ open: false })}>
                    <div className="video-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="video-modal-header">
                            <h3>📺 {videoModal.title}</h3>
                            <button className="video-modal-close" onClick={() => setVideoModal({ open: false })}>✕</button>
                        </div>
                        <div className="video-modal-body">
                            {extractYouTubeId(videoModal.url) ? (
                                <iframe
                                    width="100%"
                                    height="315"
                                    src={`https://www.youtube.com/embed/${extractYouTubeId(videoModal.url)}`}
                                    title={videoModal.title}
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                />
                            ) : (
                                <div className="video-error">
                                    <p>⚠️ Unable to load video. You can watch it directly here:</p>
                                    <a href={videoModal.url} target="_blank" rel="noopener noreferrer">📺 Click to watch on YouTube</a>
                                </div>
                            )}
                        </div>
                        <div className="video-modal-footer">
                            <button className="video-watch-btn" onClick={() => setVideoModal({ open: false })}>Got it! Continue Practice</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GamifiedPractice;